import { useState, useEffect } from 'react'
import { 
  Settings as SettingsIcon, 
  Users, 
  Mail, 
  Shield, 
  Send,
  Loader2,
  Check,
  X,
  UserCog,
  Bell,
  FileSpreadsheet,
  ToggleLeft,
  ToggleRight,
  Lock,
  Trash2
} from 'lucide-react'
import api, { getErrorMessage } from '../utils/api'
import { useAuth } from '../contexts/AuthContext'

export default function Settings() {
  const { user: currentUser } = useAuth()
  const isAdmin = currentUser?.role === 'admin'
  
  const [activeTab, setActiveTab] = useState('users')
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [notification, setNotification] = useState(null)
  
  // 報表通知設定
  const [notifySettings, setNotifySettings] = useState({
    enabled: true,
    notifyUsers: [] // 選擇要通知的用戶 ID 列表
  })
  const [savingSettings, setSavingSettings] = useState(false)

  useEffect(() => {
    fetchUsers()
    fetchNotifySettings()
  }, [])

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users')
      setUsers(response.data.users || [])
    } catch (error) {
      console.error('Failed to fetch users:', error)
    }
  }

  const fetchNotifySettings = async () => {
    try {
      const response = await api.get('/settings/notify')
      if (response.data.success) {
        setNotifySettings(response.data.settings || { enabled: true, notifyUsers: [] })
      }
    } catch (error) {
      console.error('Failed to fetch notify settings:', error)
    }
  }

  const updateUserRole = async (userId, newRole) => {
    if (!isAdmin) {
      showNotification('error', '只有管理員可以修改權限')
      return
    }
    
    // 不能修改自己的權限
    if (currentUser?.id === userId) {
      showNotification('error', '不能修改自己的權限')
      return
    }
    
    try {
      setLoading(true)
      await api.put(`/users/${userId}/role`, { role: newRole })
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u))
      showNotification('success', '權限已更新')
    } catch (error) {
      showNotification('error', getErrorMessage(error, '更新失敗'))
    } finally {
      setLoading(false)
    }
  }

  const deleteUser = async (userId, userName) => {
    if (!isAdmin) {
      showNotification('error', '只有管理員可以刪除用戶')
      return
    }
    
    // 不能刪除自己
    if (currentUser?.id === userId) {
      showNotification('error', '不能刪除自己的帳號')
      return
    }
    
    // 確認刪除
    if (!window.confirm(`確定要刪除用戶「${userName}」嗎？\n\n此操作無法復原！`)) {
      return
    }
    
    try {
      setLoading(true)
      await api.delete(`/users/${userId}`)
      setUsers(users.filter(u => u.id !== userId))
      showNotification('success', `用戶 ${userName} 已刪除`)
    } catch (error) {
      showNotification('error', getErrorMessage(error, '刪除失敗'))
    } finally {
      setLoading(false)
    }
  }

  const toggleNotifyUser = (userId) => {
    const currentList = notifySettings.notifyUsers || []
    const newList = currentList.includes(userId)
      ? currentList.filter(id => id !== userId)
      : [...currentList, userId]
    
    setNotifySettings({ ...notifySettings, notifyUsers: newList })
  }

  const saveNotifySettings = async () => {
    try {
      setSavingSettings(true)
      await api.post('/settings/notify', notifySettings)
      showNotification('success', '通知設定已儲存')
    } catch (error) {
      showNotification('error', getErrorMessage(error, '儲存失敗'))
    } finally {
      setSavingSettings(false)
    }
  }

  const testSendReport = async () => {
    if (notifySettings.notifyUsers.length === 0) {
      showNotification('error', '請先選擇要通知的用戶')
      return
    }
    
    try {
      setLoading(true)
      const response = await api.post('/email/test-report')
      if (response.data.success) {
        showNotification('success', '測試報表已發送')
      } else {
        showNotification('error', response.data.message || '發送失敗')
      }
    } catch (error) {
      showNotification('error', getErrorMessage(error, '發送失敗'))
    } finally {
      setLoading(false)
    }
  }

  const showNotification = (type, message) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 3000)
  }

  const tabs = [
    { id: 'users', name: '用戶權限管理', icon: UserCog },
    { id: 'notify', name: '報表通知設定', icon: Bell },
  ]

  return (
    <div className="p-6">
      {/* 通知 */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 ${
          notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {notification.type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
          <span>{notification.message}</span>
        </div>
      )}

      {/* 頁面標題 */}
      <div className="flex items-center space-x-3 mb-6">
        <SettingsIcon className="w-8 h-8 text-gray-700" />
        <h1 className="text-2xl font-bold text-gray-900">系統設定</h1>
      </div>

      {/* 頁籤 */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6 w-fit">
        {tabs.map(tab => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-indigo-600 shadow'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="font-medium">{tab.name}</span>
            </button>
          )
        })}
      </div>

      {/* 用戶權限管理 */}
      {activeTab === 'users' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">登入用戶權限管理</h2>
              </div>
              <p className="text-sm text-gray-500 mt-1">管理透過 OAuth 登入的系統用戶權限</p>
            </div>
            {!isAdmin && (
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Lock className="w-4 h-4 text-yellow-600" />
                <span className="text-sm text-yellow-700">只有管理員可以修改權限</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* 左欄：管理員 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 bg-purple-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-purple-600" />
                    <h3 className="font-semibold text-purple-900">管理員</h3>
                  </div>
                  <span className="text-xs font-medium bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                    {users.filter(u => u.role === 'admin').length} 人
                  </span>
                </div>
                <p className="text-xs text-purple-600 mt-1">擁有完整系統管理權限</p>
              </div>
              <div className="divide-y divide-gray-100">
                {users.filter(u => u.role === 'admin').length === 0 ? (
                  <div className="px-5 py-8 text-center text-gray-400 text-sm">尚無管理員</div>
                ) : (
                  users.filter(u => u.role === 'admin').map(user => (
                    <UserCard
                      key={user.id}
                      user={user}
                      currentUser={currentUser}
                      isAdmin={isAdmin}
                      loading={loading}
                      onRoleChange={updateUserRole}
                      onDelete={deleteUser}
                    />
                  ))
                )}
              </div>
            </div>

            {/* 右欄：一般用戶 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 bg-blue-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    <h3 className="font-semibold text-blue-900">一般用戶</h3>
                  </div>
                  <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {users.filter(u => u.role !== 'admin').length} 人
                  </span>
                </div>
                <p className="text-xs text-blue-600 mt-1">標準存取權限</p>
              </div>
              <div className="divide-y divide-gray-100">
                {users.filter(u => u.role !== 'admin').length === 0 ? (
                  <div className="px-5 py-8 text-center text-gray-400 text-sm">尚無一般用戶</div>
                ) : (
                  users.filter(u => u.role !== 'admin').map(user => (
                    <UserCard
                      key={user.id}
                      user={user}
                      currentUser={currentUser}
                      isAdmin={isAdmin}
                      loading={loading}
                      onRoleChange={updateUserRole}
                      onDelete={deleteUser}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 報表通知設定 */}
      {activeTab === 'notify' && (
        <div className="space-y-6">
          {/* 功能說明 */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <FileSpreadsheet className="w-6 h-6 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">自動報表通知功能</h3>
                <p className="text-sm text-blue-700 mt-1">
                  當新的費用報表上傳後，系統會自動產生包含「剩餘費用」和「使用量分布」的 Excel 報表，
                  並發送郵件通知給下方勾選的用戶。
                </p>
              </div>
            </div>
          </div>

          {/* 啟用開關 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">啟用自動通知</h3>
                <p className="text-sm text-gray-500 mt-1">上傳報表後自動發送郵件給選定的用戶</p>
              </div>
              <button
                onClick={() => isAdmin && setNotifySettings({ ...notifySettings, enabled: !notifySettings.enabled })}
                disabled={!isAdmin}
                className={`p-1 rounded-full transition-colors ${
                  notifySettings.enabled ? 'text-green-600' : 'text-gray-400'
                } ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {notifySettings.enabled ? (
                  <ToggleRight className="w-10 h-10" />
                ) : (
                  <ToggleLeft className="w-10 h-10" />
                )}
              </button>
            </div>
          </div>

          {/* 選擇通知用戶 */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Mail className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-900">選擇接收通知的用戶</h2>
              </div>
              <p className="text-sm text-gray-500 mt-1">勾選要接收報表通知郵件的系統用戶</p>
            </div>

            <div className="p-6">
              {users.filter(u => u.is_active).length === 0 ? (
                <p className="text-center text-gray-500 py-4">目前沒有用戶</p>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 左欄：管理員 */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-200 bg-purple-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Shield className="w-4 h-4 text-purple-600" />
                          <h3 className="font-semibold text-purple-900">管理員</h3>
                        </div>
                        <span className="text-xs font-medium bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                          {users.filter(u => u.is_active && u.role === 'admin').length} 人
                        </span>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {users.filter(u => u.is_active && u.role === 'admin').length === 0 ? (
                        <div className="px-5 py-6 text-center text-gray-400 text-sm">尚無管理員</div>
                      ) : (
                        users.filter(u => u.is_active && u.role === 'admin').map(user => (
                          <label
                            key={user.id}
                            className={`flex items-center px-4 py-3 cursor-pointer transition-all ${
                              (notifySettings.notifyUsers || []).includes(user.id)
                                ? 'bg-purple-50'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={(notifySettings.notifyUsers || []).includes(user.id)}
                              onChange={() => isAdmin && toggleNotifyUser(user.id)}
                              disabled={!isAdmin}
                              className={`w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                            />
                            <div className="ml-3 flex items-center space-x-3 flex-1 min-w-0">
                              {user.avatar_url ? (
                                <img src={user.avatar_url} alt={user.name} className="w-8 h-8 rounded-full flex-shrink-0" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                  <Shield className="w-4 h-4 text-purple-600" />
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">{user.name || '未命名'}</p>
                                <p className="text-xs text-gray-500 truncate">{user.email}</p>
                              </div>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {/* 右欄：一般用戶 */}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-200 bg-blue-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4 text-blue-600" />
                          <h3 className="font-semibold text-blue-900">一般用戶</h3>
                        </div>
                        <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          {users.filter(u => u.is_active && u.role !== 'admin').length} 人
                        </span>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {users.filter(u => u.is_active && u.role !== 'admin').length === 0 ? (
                        <div className="px-5 py-6 text-center text-gray-400 text-sm">尚無一般用戶</div>
                      ) : (
                        users.filter(u => u.is_active && u.role !== 'admin').map(user => (
                          <label
                            key={user.id}
                            className={`flex items-center px-4 py-3 cursor-pointer transition-all ${
                              (notifySettings.notifyUsers || []).includes(user.id)
                                ? 'bg-blue-50'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={(notifySettings.notifyUsers || []).includes(user.id)}
                              onChange={() => isAdmin && toggleNotifyUser(user.id)}
                              disabled={!isAdmin}
                              className={`w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                            />
                            <div className="ml-3 flex items-center space-x-3 flex-1 min-w-0">
                              {user.avatar_url ? (
                                <img src={user.avatar_url} alt={user.name} className="w-8 h-8 rounded-full flex-shrink-0" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                  <Users className="w-4 h-4 text-blue-600" />
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">{user.name || '未命名'}</p>
                                <p className="text-xs text-gray-500 truncate">{user.email}</p>
                              </div>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 操作按鈕 */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              {!isAdmin && (
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <Lock className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm text-yellow-700">只有管理員可以修改通知設定</span>
                </div>
              )}
              {isAdmin && (
                <button
                  onClick={testSendReport}
                  disabled={loading || (notifySettings.notifyUsers || []).length === 0}
                  className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:text-gray-400"
                >
                  <Send className="w-4 h-4" />
                  <span>發送測試報表</span>
                </button>
              )}
              
              <button
                onClick={saveNotifySettings}
                disabled={savingSettings || !isAdmin}
                className={`flex items-center space-x-2 px-6 py-2 font-medium rounded-lg transition-colors disabled:opacity-50 ${isAdmin ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              >
                {savingSettings ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>儲存中...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>儲存設定</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  )
}

// 用戶卡片元件
function UserCard({ user, currentUser, isAdmin, loading, onRoleChange, onDelete }) {
  const isSelf = currentUser?.id === user.id
  const providerLower = (user.provider || '').toLowerCase()

  return (
    <div className="px-5 py-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-3">
        {/* 頭像 */}
        {user.avatar_url ? (
          <img src={user.avatar_url} alt={user.name} className="w-10 h-10 rounded-full flex-shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-indigo-600" />
          </div>
        )}

        {/* 名稱 + Email */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-gray-900 truncate">{user.name || '未命名'}</span>
            {isSelf && <span className="text-xs text-indigo-600 flex-shrink-0">(自己)</span>}
          </div>
          <p className="text-xs text-gray-500 truncate">{user.email}</p>
        </div>

        {/* 登入方式 */}
        <div className="flex-shrink-0">
          {providerLower === 'microsoft' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
              <svg className="w-3.5 h-3.5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              MS
            </span>
          ) : providerLower === 'google' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700 rounded-full">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Google
            </span>
          ) : (
            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
              {user.provider || 'OAuth'}
            </span>
          )}
        </div>
      </div>

      {/* 底部：最後登入 + 操作 */}
      <div className="mt-2.5 ml-[52px] flex items-center justify-between">
        <span className="text-xs text-gray-400">
          {user.last_login_at
            ? `最後登入 ${new Date(user.last_login_at).toLocaleString('zh-TW')}`
            : '尚未登入'}
        </span>
        <div className="flex items-center gap-2">
          {/* 權限切換 */}
          <select
            value={user.role || 'user'}
            onChange={(e) => onRoleChange(user.id, e.target.value)}
            disabled={loading || !isAdmin || isSelf}
            className={`text-xs border rounded-md px-1.5 py-1 ${
              (!isAdmin || isSelf)
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-white border-gray-300 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500'
            }`}
          >
            <option value="admin">管理員</option>
            <option value="user">一般用戶</option>
          </select>
          {/* 刪除 */}
          {isAdmin && !isSelf ? (
            <button
              onClick={() => onDelete(user.id, user.name || user.email)}
              disabled={loading}
              className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded"
              title="刪除用戶"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
